import React, {useEffect, useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {exportTimesheet, fetchTimesheetFields} from '@/entities/attendance/api';
import type {Period} from '@/entities/attendance/period';
import {
  TIMESHEET_SORTS,
  type TimesheetCatalog,
  type TimesheetField,
  type TimesheetSort,
} from '@/entities/attendance/types';
import {CAN_DOWNLOAD, saveResponseAsFile} from '@/shared/lib/download';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {HelpNote} from '@/shared/ui/explain';
import {Icon} from '@/shared/ui/icons';
import {Segmented} from '@/shared/ui/segmented';
import {SkeletonList} from '@/shared/ui/skeleton';
import {AppText} from '@/shared/ui/text';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {controls, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Табель по выбранным параметрам — третий, самый поздний способ унести отчёт из
 * кабинета, и единственный, где состав файла заказывает читатель.
 *
 * ## Чем он отличается от двух соседних кнопок
 *
 * `/control/attendance/export` отдаёт три ФИКСИРОВАННЫХ листа плоскими таблицами:
 * строка — это человек или человек-день. Табель — МАТРИЦА: слева человек, вправо числа
 * месяца, справа итоги, в клетке приход и уход. Это то, что печатают, подписывают и
 * сдают бухгалтеру, и то, чего плоская таблица не заменяет.
 *
 * ## Галочки приходят с сервера, а не отсюда
 *
 * Список полей НЕ зашит в этом файле, и это не лень: справочник отдаёт только то, на что
 * у этого вызывающего есть право — у кого нет `salaries`, тот зарплатных полей и листа
 * ведомости не увидит вовсе. Отсюда два следствия. Кабинету не нужно знать про права
 * (нечего показывать — нечего и прятать), а новое поле на сервере появляется в галочках
 * без нашего релиза.
 *
 * Ключи в запрос уходят ТОЛЬКО из справочника. Незнакомый ключ маршрут отвергает
 * четыреста двадцать вторым с именем ключа, а не игнорирует молча — и это правильно:
 * у системы уже был случай, когда `?search=` вместо `?q=` прочиталось как «отдай всё».
 *
 * ## Почему CSV — вторая кнопка, а не второй формат
 *
 * `format=csv` табель отвергает, и это решение, а не пробел: матрица с двумя строками в
 * клетке в CSV не ложится, а отдать вместо неё плоский лист под тем же именем значило бы
 * выдать не то, что просили. Поэтому CSV здесь честно уезжает ПРЕЖНЕЙ ручкой — за тот же
 * период и те же отделы, — и подпись под кнопками говорит, что придёт другой файл, а не
 * тот же в другом расширении.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  body: {maxHeight: 360},
  group: {gap: 2, paddingTop: spacing(2)},
  groupHead: {paddingHorizontal: spacing(1), paddingBottom: spacing(1)},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    minHeight: controls.md,
  },
  /* Лист открывают с телефона, а в вебе `hitSlop` не работает: касаемая область —
     это сама строка, и на телефоне она держит 44pt. */
  rowPhone: {minHeight: controls.lg},
  rowText: {flex: 1, minWidth: 0},
  check: {
    width: 20,
    height: 20,
    borderRadius: radii.sm,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  head: {gap: 2},
  buttons: {flexDirection: 'row', gap: spacing(2), flexWrap: 'wrap'},
  button: {flexGrow: 1, flexBasis: 150},
  sortRow: {gap: spacing(2)},
});

/** True, пока указатель над строкой или палец на ней. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

/**
 * Одна галочка.
 *
 * Заблокированная (ФИО, лист расшифровки) рисуется ОТМЕЧЕННОЙ и приглушённой, а не
 * прячется: читатель должен видеть, что в файле она будет, — иначе он ищет её в списке
 * и не находит.
 */
function CheckRow({
  field,
  checked,
  onToggle,
  lockedHint,
}: {
  field: TimesheetField;
  checked: boolean;
  onToggle: () => void;
  lockedHint: string;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const locked = field.locked;

  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{checked, disabled: locked}}
      accessibilityLabel={field.title}
      disabled={locked}
      onPress={onToggle}
      testID={`timesheet-field-${field.key}`}
      style={state => [
        styles.row,
        !isDesktop && styles.rowPhone,
        {
          backgroundColor: checked
            ? p.accent
            : isHot(state)
            ? p.muted
            : 'transparent',
        },
        locked && {opacity: 0.7},
      ]}>
      <View
        style={[
          styles.check,
          {
            backgroundColor: checked ? p.primary : 'transparent',
            borderColor: checked ? p.primary : p.input,
          },
        ]}>
        {checked ? <Icon name="check" size={14} color={p.primaryForeground} /> : null}
      </View>
      <View style={styles.rowText}>
        <AppText
          variant="body"
          numberOfLines={2}
          color={checked ? p.accentForeground : p.foreground}>
          {field.title}
        </AppText>
        {locked ? <AppText variant="caption">{lockedHint}</AppText> : null}
      </View>
    </Pressable>
  );
}

/** Ключи, отмеченные по умолчанию — то, с чего справочник предлагает начать. */
function defaults(items: TimesheetField[]): string[] {
  return items.filter(f => f.defaultOn || f.locked).map(f => f.key);
}

export function TimesheetPicker({
  companyId,
  period,
  /** Уже отформатированный период — им подписывается файл и строка «что внутри». */
  periodLabel,
  departmentIds,
  recordIds,
  /** Без расширения — обработчик дописывает своё. */
  fileName,
  /**
   * Плоский CSV за тот же период. Хозяин экрана решает, какой ручкой: у компании это
   * `/control/attendance/export`, у отдела — его собственная. Не передан — кнопки CSV
   * нет, и это честнее неработающей.
   */
  onCsv,
}: {
  companyId: string;
  period: Period;
  periodLabel: string;
  departmentIds?: string[];
  recordIds?: string[];
  fileName: string;
  onCsv?: () => Promise<Response>;
}) {
  const {t, i18n} = useTranslation();
  const p = useTheme();

  const [fields, setFields] = useState<string[] | null>(null);
  const [sheets, setSheets] = useState<string[] | null>(null);
  const [sort, setSort] = useState<TimesheetSort>('department');
  const [busy, setBusy] = useState<'xlsx' | 'csv' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const catalog = useQuery({
    queryKey: ['timesheet-fields', companyId, i18n.language],
    queryFn: () => fetchTimesheetFields(companyId, i18n.language),
    // Справочник меняется с правами и релизами сервера, а не в течение сеанса.
    staleTime: 5 * 60_000,
  });

  // Отметки заводятся один раз на справочник. `null` до этого момента — это «ещё не
  // выбирали», и оно отличается от «сняли всё»: иначе первый же рендер до ответа
  // записал бы пустой выбор и затёр умолчания сервера.
  const data: TimesheetCatalog | undefined = catalog.data;
  useEffect(() => {
    if (!data) {
      return;
    }
    setFields(defaults(data.fields));
    setSheets(defaults(data.sheets));
  }, [data]);

  const chosenFields = fields ?? (data ? defaults(data.fields) : []);
  const chosenSheets = sheets ?? (data ? defaults(data.sheets) : []);

  const grouped = useMemo(() => {
    if (!data) {
      return [];
    }
    // Порядок групп — серверный: он же порядок колонок в книге, и переставлять его
    // здесь значило бы обещать в интерфейсе не тот файл, который приедет.
    return data.groups
      .map(g => ({group: g, items: data.fields.filter(f => f.group === g.key)}))
      .filter(block => block.items.length > 0);
  }, [data]);

  const toggle = (list: string[], key: string): string[] =>
    list.includes(key) ? list.filter(k => k !== key) : [...list, key];

  const runXlsx = async () => {
    setBusy('xlsx');
    setError(null);
    try {
      const res = await exportTimesheet(companyId, period, {
        departmentIds,
        recordIds,
        fields: chosenFields,
        sheets: chosenSheets,
        sort,
        lang: i18n.language,
      });
      await saveResponseAsFile(res, `${fileName}.xlsx`);
    } catch {
      setError(t('timesheet.failed'));
    } finally {
      setBusy(null);
    }
  };

  const runCsv = async () => {
    if (!onCsv) {
      return;
    }
    setBusy('csv');
    setError(null);
    try {
      await saveResponseAsFile(await onCsv(), `${fileName}.csv`);
    } catch {
      setError(t('timesheet.failed'));
    } finally {
      setBusy(null);
    }
  };

  // На телефоне файл положить некуда — shared/lib/download говорит это честно, вместо
  // кнопки, которая бросит исключение.
  if (!CAN_DOWNLOAD) {
    return <HelpNote text={t('reports.exportWebOnly')} />;
  }

  if (catalog.isPending) {
    return <SkeletonList count={4} avatar={false} />;
  }

  if (catalog.isError || !data) {
    return (
      <View style={styles.root}>
        <Banner tone="error" title={t('timesheet.catalogError')} />
        <AppButton
          title={t('actions.retry')}
          tone="outline"
          size="sm"
          onPress={() => {
            void catalog.refetch();
          }}
          testID="timesheet-retry"
        />
      </View>
    );
  }

  return (
    <View style={styles.root} testID="timesheet-picker">
      <View style={styles.head}>
        <AppText variant="body">
          {t('timesheet.contents', {
            period: periodLabel,
            fields: chosenFields.length,
            sheets: chosenSheets.length,
          })}
        </AppText>
        <AppText variant="mono" color={p.mutedForeground} numberOfLines={1}>
          {fileName}.xlsx
        </AppText>
      </View>

      <View style={styles.sortRow}>
        <AppText variant="eyebrow">{t('timesheet.sortTitle')}</AppText>
        <Segmented
          value={sort}
          onChange={setSort}
          testID="timesheet-sort"
          options={TIMESHEET_SORTS.map(key => ({
            value: key,
            label: t(`timesheet.sort.${key}`),
          }))}
        />
      </View>

      <View style={styles.body}>
        <ScrollView showsVerticalScrollIndicator>
          {grouped.map(block => (
            <View key={block.group.key} style={styles.group}>
              <View style={styles.groupHead}>
                <AppText variant="eyebrow">{block.group.title}</AppText>
              </View>
              {block.items.map(field => (
                <CheckRow
                  key={field.key}
                  field={field}
                  checked={chosenFields.includes(field.key)}
                  lockedHint={t('timesheet.locked')}
                  onToggle={() => setFields(toggle(chosenFields, field.key))}
                />
              ))}
            </View>
          ))}

          <View style={styles.group}>
            <View style={styles.groupHead}>
              <AppText variant="eyebrow">{t('timesheet.sheetsTitle')}</AppText>
            </View>
            {data.sheets.map(sheet => (
              <CheckRow
                key={sheet.key}
                field={sheet}
                checked={chosenSheets.includes(sheet.key)}
                lockedHint={t('timesheet.locked')}
                onToggle={() => setSheets(toggle(chosenSheets, sheet.key))}
              />
            ))}
          </View>
        </ScrollView>
      </View>

      <View style={styles.buttons}>
        <AppButton
          title={t('timesheet.downloadXlsx')}
          tone="soft"
          size="sm"
          loading={busy === 'xlsx'}
          disabled={busy !== null || chosenFields.length === 0}
          onPress={() => void runXlsx()}
          style={styles.button}
          testID="timesheet-xlsx"
        />
        {onCsv ? (
          <AppButton
            title={t('timesheet.downloadCsv')}
            tone="neutral"
            size="sm"
            loading={busy === 'csv'}
            disabled={busy !== null}
            onPress={() => void runCsv()}
            style={styles.button}
            testID="timesheet-csv"
          />
        ) : null}
      </View>

      {/* Две вещи, о которых лучше узнать до печати, а не после подписи. */}
      {onCsv ? <HelpNote text={t('timesheet.csvNote')} /> : null}
      <HelpNote text={t('timesheet.absenceNote')} />

      {error ? <Banner tone="error" title={error} /> : null}
    </View>
  );
}
